import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import ProductGroup from '../../../../../components/common/ProductGroup';
import CarItemCodeDropdown from '../../../../../components/common/CarItemCodeDropdown';
import UserDropdown from '../../../../../components/common/UserDropdown';
import { validateDateRange } from '../../../../../utils/dateUtils';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { Dropdown } from 'primereact/dropdown';

const CURRENCY_OPTIONS = [
    { label: 'TL', value: 'TL' },
    { label: 'USD', value: 'USD' },
    { label: 'EUR', value: 'EUR' }
];

const INITIAL_STATE = {
    startDate: null,
    endDate: null,
    productGroup: '',
    carItemCode: '',
    warrClaimLimits: 0,
    warrClaimLimitsCurrUnits: '',
    responsibleList: []
};

const ClaimLimitDialog = ({ visible, onHide, onSubmit, initialData, isEdit, loading }) => {
    const { th } = useLanguage();
    const [formData, setFormData] = useState(INITIAL_STATE);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isEdit && initialData) {
            const parseDate = (dateValue) => {
                if (!dateValue) return null;
                const date = new Date(dateValue);

                if (isNaN(date.getTime()) || date.getFullYear() === 1970) {
                    return null;
                }
                return date;
            };

            const transformedData = {
                ...initialData,
                startDate: parseDate(initialData.startDate),
                endDate: parseDate(initialData.endDate),
                responsibleList:
                    initialData.responsibleList?.map((user) => ({
                        id: user.id,
                        name: user.name
                    })) || []
            };
            setFormData(transformedData);
        } else {
            resetForm();
        }
    }, [isEdit, initialData, visible]);

    const resetForm = () => {
        setFormData(INITIAL_STATE);
        setErrors({});
    };

    const handleStartDateChange = (value) => {
        const newFormData = { ...formData, startDate: value };
        setFormData(newFormData);

        // Merkezi validateDateRange fonksiyonunu kullan
        const validation = validateDateRange(value, newFormData.endDate);
        const newErrors = { ...errors };

        if (!validation.isValid) {
            newErrors.startDate = validation.error;
            newErrors.endDate = validation.error;
        } else {
            delete newErrors.startDate;
            delete newErrors.endDate;
        }

        setErrors(newErrors);
    };

    const handleEndDateChange = (value) => {
        const newFormData = { ...formData, endDate: value };
        setFormData(newFormData);

        // Merkezi validateDateRange fonksiyonunu kullan
        const validation = validateDateRange(newFormData.startDate, value);
        const newErrors = { ...errors };

        if (!validation.isValid) {
            newErrors.startDate = validation.error;
            newErrors.endDate = validation.error;
        } else {
            delete newErrors.startDate;
            delete newErrors.endDate;
        }

        setErrors(newErrors);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.startDate) {
            newErrors.startDate = th('warrantyClaimLimit.validation.startDateRequired');
        }

        if (!formData.warrClaimLimits || formData.warrClaimLimits <= 0) {
            newErrors.warrClaimLimits = th('warrantyClaimLimit.validation.warrClaimLimitsRequired');
        }

        if (!formData.warrClaimLimitsCurrUnits) {
            newErrors.warrClaimLimitsCurrUnits = th('warrantyClaimLimit.validation.currencyRequired');
        }

        // Merkezi validateDateRange fonksiyonunu kullan - sadece her iki tarih de varsa
        if (formData.startDate && formData.endDate) {
            const validation = validateDateRange(formData.startDate, formData.endDate);
            if (!validation.isValid) {
                newErrors.startDate = validation.error;
                newErrors.endDate = validation.error;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        try {
            console.log('info', formData.startDate);
            console.log('info', formData.startDate.toISOString());
            const submitData = {
                ...formData,
                startDate: formData.startDate.toISOString(),
                endDate: formData.endDate ? formData.endDate.toISOString() : null,
                responsibleList: formData.responsibleList.map((user) => ({
                    id: user.id,
                    name: user.name
                }))
            };
            if (isEdit) {
                submitData.id = initialData.id;
            }

            // Remove groupId from payload if it exists
            if (submitData.groupId) {
                delete submitData.groupId;
            }

            const response = await onSubmit(submitData);

            if (response?.isSuccessful) {
                resetForm();
                onHide();
            }
        } catch (error) {
            console.error('Form submission error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const footer = (
        <div>
            <Button
                label={th('warrantyClaimLimit.buttons.cancel')}
                icon="pi pi-times"
                outlined
                onClick={() => {
                    resetForm();
                    onHide();
                }}
                disabled={isSubmitting}
            />
            <Button label={th('warrantyClaimLimit.buttons.save')} icon="pi pi-check" onClick={handleSubmit} autoFocus loading={isSubmitting} disabled={isSubmitting} />
        </div>
    );

    const renderFieldError = (fieldName) => {
        return errors[fieldName] ? <small className="p-error block mt-1">{errors[fieldName]}</small> : null;
    };

    return (
        <Dialog
            visible={visible}
            style={{ width: '450px' }}
            header={isEdit ? th('warrantyClaimLimit.dialogHeader.edit') : th('warrantyClaimLimit.dialogHeader.create')}
            modal
            className="p-fluid"
            footer={footer}
            onHide={() => {
                resetForm();
                onHide();
            }}
        >
            <div className="field">
                <label htmlFor="startDate">{th('warrantyClaimLimit.formLabels.startDate')} *</label>
                <Calendar
                    id="startDate"
                    value={formData.startDate}
                    onChange={(e) => handleStartDateChange(e.value)}
                    dateFormat="dd/mm/yy"
                    showIcon
                    maxDate={formData.endDate}
                    className={errors.startDate ? 'p-invalid' : ''}
                    placeholder={th('warrantyClaimLimit.placeholders.startDate')}
                />
                {renderFieldError('startDate')}
            </div>

            <div className="field">
                <label htmlFor="endDate">{th('warrantyClaimLimit.formLabels.endDate')}</label>
                <Calendar id="endDate" value={formData.endDate} onChange={(e) => handleEndDateChange(e.value)} dateFormat="dd/mm/yy" showIcon minDate={formData.startDate} className="" placeholder={th('warrantyClaimLimit.placeholders.endDate')} />
            </div>

            <div className="field">
                <label htmlFor="productGroup">{th('warrantyClaimLimit.formLabels.productGroup')}</label>
                <ProductGroup value={formData.productGroup} onChange={(value) => setFormData({ ...formData, productGroup: value })} placeholder={th('warrantyClaimLimit.placeholders.productGroup')} />
            </div>

            <div className="field">
                <label htmlFor="carItemCode">{th('warrantyClaimLimit.formLabels.carItemCode')}</label>
                <CarItemCodeDropdown
                    value={formData.carItemCode}
                    onChange={(value) => setFormData({ ...formData, carItemCode: value })}
                    disabled={!formData.productGroup}
                    productGroup={formData.productGroup}
                    placeholder={th('warrantyClaimLimit.placeholders.carItemCode')}
                />
            </div>

            <div className="field">
                <label htmlFor="warrClaimLimits">{th('warrantyClaimLimit.formLabels.warrClaimLimits')} *</label>
                <InputNumber
                    id="warrClaimLimits"
                    value={formData.warrClaimLimits}
                    onValueChange={(e) => setFormData({ ...formData, warrClaimLimits: e.value })}
                    min={0}
                    className={errors.warrClaimLimits ? 'p-invalid' : ''}
                    placeholder={th('warrantyClaimLimit.placeholders.warrClaimLimits')}
                />
                {renderFieldError('warrClaimLimits')}
            </div>

            <div className="field">
                <label htmlFor="warrClaimLimitsCurrUnits">{th('warrantyClaimLimit.formLabels.warrClaimLimitsCurrUnits')} *</label>
                <Dropdown
                    id="currency"
                    value={formData.warrClaimLimitsCurrUnits}
                    options={CURRENCY_OPTIONS}
                    onChange={(e) => setFormData({ ...formData, warrClaimLimitsCurrUnits: e.value })}
                    placeholder={th('warrantyClaimLimit.placeholders.currency')}
                    className={errors.warrClaimLimitsCurrUnits ? 'p-invalid' : ''}
                />
                {renderFieldError('warrClaimLimitsCurrUnits')}
            </div>

            <div className="field">
                <label htmlFor="responsibleList">{th('warrantyClaimLimit.formLabels.responsibleList')}</label>
                <UserDropdown
                    value={formData.responsibleList}
                    onChange={(selectedUsers) => {
                        setFormData((prev) => ({
                            ...prev,
                            responsibleList: selectedUsers
                        }));
                    }}
                    placeholder={th('warrantyClaimLimit.placeholders.responsible')}
                />
            </div>
        </Dialog>
    );
};

export default ClaimLimitDialog;
